import { Skeleton } from '@/components/ui/skeleton'
import { cn, timeAgo } from '@/lib/utils'
import { getLiveOrgAgents, type OrgAgent } from '@/lib/agent-org'
import {
  computeAgentStatus,
  agentStatusMeta,
  countStatuses,
  type AgentStatus,
} from '@/lib/agent-status'
import { getAgentIcon } from '@/components/agents/AgentIcons'
import type { AgentSummary } from '@/lib/api'

interface AgentStatusGridProps {
  agents: AgentSummary[]
  isLoading: boolean
}

/**
 * Compact traffic-light board for every live agent + infrastructure service —
 * the whole shipping roster (~20) visible at a glance without scrolling. Each
 * cell shows a green/amber/red/idle dot, success rate, and last activity.
 */
export function AgentStatusGrid({ agents, isLoading }: AgentStatusGridProps) {
  const liveByName = new Map<string, AgentSummary>()
  for (const a of agents) liveByName.set(a.name, a)

  const roster = getLiveOrgAgents()
  const statuses = roster.map((a) => computeAgentStatus(liveByName.get(a.skillName)))
  const counts = countStatuses(statuses)

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 20 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <StatusLegend counts={counts} />
      <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {roster.map((agent, i) => (
          <AgentCell key={agent.skillName} agent={agent} live={liveByName.get(agent.skillName)} status={statuses[i]} />
        ))}
      </div>
    </div>
  )
}

function StatusLegend({ counts }: { counts: ReturnType<typeof countStatuses> }) {
  const entries: { status: AgentStatus; n: number }[] = [
    { status: 'green', n: counts.green },
    { status: 'amber', n: counts.amber },
    { status: 'red', n: counts.red },
    { status: 'idle', n: counts.idle },
  ]
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-cmd-low px-3 py-2">
      <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
        {counts.total} Agents
      </span>
      {entries.map(({ status, n }) => {
        const meta = agentStatusMeta(status)
        return (
          <div key={status} className="flex items-center gap-1.5">
            <span className={cn('h-2 w-2 rounded-full', meta.dot)} aria-hidden="true" />
            <span className="font-mono text-[0.6rem] text-cmd-outline tabular-nums">
              {n} <span className="text-cmd-outline-variant">{meta.label}</span>
            </span>
          </div>
        )
      })}
    </div>
  )
}

function AgentCell({
  agent,
  live,
  status,
}: {
  agent: OrgAgent
  live: AgentSummary | undefined
  status: AgentStatus
}) {
  const meta = agentStatusMeta(status)
  const Icon = getAgentIcon(agent.skillName) ?? getAgentIcon(agent.displayName)
  const successRate = live?.success_rate_24h
  const isService = agent.phase === 'infrastructure'

  return (
    <div className="relative bg-cmd-container p-2.5 pl-3">
      <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ backgroundColor: agent.accent }} />
      <div className="flex items-center gap-2">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', meta.dot)} aria-hidden="true" />
        <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-cmd-high text-xs">
          {Icon ? <Icon size={22} /> : <span>{agent.emoji}</span>}
        </span>
        <div className="min-w-0">
          <p className="font-sans text-xs font-semibold text-white truncate leading-tight">
            {agent.displayName}
          </p>
          <p className="font-label text-[0.55rem] text-cmd-outline truncate uppercase">{agent.role}</p>
        </div>
        <span className="sr-only">{meta.label}</span>
      </div>
      <div className="mt-1.5 flex items-center justify-between font-mono text-[0.6rem] text-cmd-outline tabular-nums">
        <span>
          {isService
            ? 'SERVICE'
            : successRate !== null && successRate !== undefined
              ? `${successRate}%`
              : '--'}
        </span>
        <span>{timeAgo(live?.last_run_at ?? null)}</span>
      </div>
    </div>
  )
}
